import { Mail, SmartToy } from '@mui/icons-material';
import { Badge, Box, IconButton, Popover, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export function MessageButton() {
    const navigate = useNavigate();
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).navigation;
    const width = useSelector((state: AppState) => state.windowSize.width);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const [messages, setMessages] = React.useState<any[]>([]);
    const [unreadCount, setUnreadCount] = React.useState(0);

    const isMobile = width <= 700;
    const open = Boolean(anchorEl);
    const id = open ? 'messages-popover' : undefined;

    // Mock data - replace with real service later
    React.useEffect(() => {
        const mockMessages = [
            {
                id: '1',
                subject: 'Willkommen im neuen System',
                from: 'Max Mustermann',
                timestamp: new Date(Date.now() - 30 * 60 * 1000),
                read: false,
                type: 'direct',
            },
            {
                id: '2',
                subject: 'Code-Review Ergebnisse verfügbar',
                from: 'Q Developer Agent',
                timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
                read: false,
                type: 'agent',
            },
            {
                id: '3',
                subject: 'System-Update verfügbar',
                from: 'System',
                timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
                read: true,
                type: 'system',
            },
        ];
        setMessages(mockMessages);
        setUnreadCount(mockMessages.filter((msg) => !msg.read).length);
    }, []);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatTimestamp = (timestamp: Date) => {
        const now = new Date();
        const diff = now.getTime() - timestamp.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days} Tag${days > 1 ? 'e' : ''} her`;
        } else if (hours > 0) {
            return `${hours} Std. her`;
        } else {
            return 'Gerade eben';
        }
    };

    const getMessageIcon = (type: string) => {
        switch (type) {
            case 'agent':
                return (
                    <Box
                        sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.primary.light, 0.15),
                            border: `1px solid ${theme.palette.primary.main}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SmartToy sx={{ fontSize: 12, color: theme.palette.primary.light }} />
                    </Box>
                );
            default:
                return (
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: theme.palette.primary.main,
                        }}
                    />
                );
        }
    };

    return (
        <>
            <Tooltip
                title="Nachrichten"
                placement="bottom"
                disableHoverListener={isMobile || width <= theme.breakpoints.values.sm}
            >
                <IconButton
                    size="small"
                    onClick={handleClick}
                    aria-describedby={id}
                    sx={{
                        color: theme.palette.primary.main,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            transform: 'scale(1.05)',
                        },
                    }}
                >
                    <Badge badgeContent={unreadCount} color="error">
                        <Mail />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        boxShadow: theme.shadows[3],
                        borderRadius: 3,
                        border: 'none',
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
                        backdropFilter: 'blur(20px)',
                        maxWidth: 400,
                        overflow: 'hidden',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Nachrichten
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {unreadCount} ungelesen
                        </Typography>
                    </Box>

                    {messages.length > 0 ? (
                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {messages.slice(0, 5).map((message, index) => (
                                <Box
                                    key={message.id}
                                    sx={{
                                        p: 1.5,
                                        mb: 1,
                                        borderRadius: 2,
                                        bgcolor: message.read
                                            ? alpha(theme.palette.background.paper, 0.5)
                                            : alpha(theme.palette.primary.main, 0.08),
                                        border: `1px solid ${alpha(theme.palette.primary.main, message.read ? 0.05 : 0.15)}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                        },
                                    }}
                                    onClick={() => {
                                        handleClose();
                                        navigate(`/${ELinks.MESSAGES}`);
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <Box sx={{ mt: 0.5 }}>{getMessageIcon(message.type)}</Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: message.read ? 400 : 600,
                                                    color: message.read ? 'text.primary' : 'primary.main',
                                                    mb: 0.5,
                                                }}
                                                noWrap
                                            >
                                                {message.subject}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: message.read ? 400 : 500 }}
                                                >
                                                    {message.from}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatTimestamp(message.timestamp)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            Keine Nachrichten vorhanden
                        </Typography>
                    )}

                    <Box
                        sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.primary.main,
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                            onClick={() => {
                                handleClose();
                                navigate(`/${ELinks.MESSAGES}`);
                            }}
                        >
                            Alle Nachrichten anzeigen
                        </Typography>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
